/*======================================================================================================================
  * Bluebell: src/app/state/results-view/results-view.selectors.ts
  * Last Edited: 30/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { FullProject } from '@state/projects/project.interfaces';
import { selectCurrentProject, selectCurrentProjectId } from '@state/projects/project.selectors';
import {
  ResultsColumnConfig, ResultsColumnSortData,
  ResultsTableColumn, ResultsTableColumnType,
  ResultsViewListData, SortedResultsTableColumn,
} from '@state/results-view/results-view.interfaces';
import { TextColumn } from '@state/text-columns/text-columns.interfaces';
import * as fromResultsView from './results-view.reducer';

export const DEFAULT_RESULTS_COLUMNS: Pick<ResultsTableColumn, 'header'|'field'>[] = [
  {header: 'Name', field: 'name'},
  {header: 'Description', field: 'description'},
  {header: 'Status', field: 'status'},
  {header: 'Reports', field: 'reports'},
  {header: 'Comment', field: 'comment'},
  {header: 'Annotations', field: 'hasAnnotations'},
];

const getPosition = (view: ResultsViewListData, projectId: string) =>
  view.pinned_in_positions.find(p => p.project === projectId)?.position ?? Number.POSITIVE_INFINITY;


export const selectResultsViewFeature = createFeatureSelector<fromResultsView.State>(fromResultsView.featureKey);

export const selectAllResultsViews = createSelector(
  selectResultsViewFeature,
  fromResultsView.getAll
);

export const selectResultsViewEntities = createSelector(
  selectResultsViewFeature,
  fromResultsView.getEntities
);

export const selectResultsViewIds = createSelector(
  selectResultsViewFeature,
  fromResultsView.getIds
);

export const selectResultsViewCount = createSelector(
  selectResultsViewFeature,
  fromResultsView.getCount
);

export const selectResultsViewsLoading = createSelector(
  selectResultsViewFeature,
  fromResultsView.getLoading
);

export const selectSelectedResultsView = createSelector(
  selectResultsViewFeature,
  fromResultsView.getSelectedView
);

export const selectSelectedResultsViewLoading = createSelector(
  selectResultsViewFeature,
  fromResultsView.getSelectedViewLoading
);

export const selectCurrentViewTextColumns = createSelector(
  selectResultsViewFeature,
  fromResultsView.getTextColumns
);

export const selectCurrentViewColumnConfigs = createSelector(
  selectResultsViewFeature,
  fromResultsView.getColumnConfigs
);

export const selectCurrentViewDeltaBaseline = createSelector(
  selectResultsViewFeature,
  fromResultsView.getDeltaBaseline
);

export const selectCurrentViewDeltaToLeadCase = createSelector(
  selectResultsViewFeature,
  fromResultsView.getDeltaToLeadCase
);

export const selectCurrentViewSortData = createSelector(
  selectResultsViewFeature,
  fromResultsView.getSortData
);

export const selectReportsResultsView = createSelector(
  selectResultsViewFeature,
  fromResultsView.getReportResultsView
);

export const selectCurrentViewHideEditColumn = createSelector(
  selectResultsViewFeature,
  fromResultsView.getHideEditColumn
);


export const selectCurrentView = createSelector(
  selectCurrentViewTextColumns,
  selectCurrentViewColumnConfigs,
  selectCurrentViewDeltaBaseline,
  selectCurrentViewDeltaToLeadCase,
  selectCurrentViewSortData,
  selectCurrentViewHideEditColumn,
  (
    textColumns,
    columns,
    deltaBaseline,
    deltaToLeadCase,
    sortData,
    hideEditColumn,
  ) => ({
    textColumns,
    columns,
    deltaBaseline,
    deltaToLeadCase,
    sortData,
    hideEditColumn,
  })
);

/**
 * Complex Selectors
 */

export const selectProjectDefaultResultsView = createSelector(
  selectCurrentProject,
  selectAllResultsViews,
  (project: FullProject|null, views: ResultsViewListData[]) => views.find(view => view.uuid === project?.default_force_table_settings)
);

export const selectPinnedResultsViews = createSelector(
  selectCurrentProjectId,
  selectAllResultsViews,
  (project: string | null, views: ResultsViewListData[]) => project
    ? views
      .filter(view => (view?.pinned_in_positions || [])
          .map(p => p.project).includes(project))
      .sort((a, b) =>
        getPosition(a, project) - getPosition(b, project)
      )
    : []
);

export const selectUnpinnedResultsViews = createSelector(
  selectCurrentProjectId,
  selectAllResultsViews,
  (project: string|null, views: ResultsViewListData[]) => project
    ? views
      .filter(view => !(view?.pinned_in_positions || [])
        .map(p => p.project).includes(project))
    : []
);

export const selectLastUsedResultsView = createSelector(
  selectAllResultsViews,
  (views: ResultsViewListData[]) => views.find(view => view.last_used)
);

export const selectResultsTableColumns = createSelector(
  selectCurrentViewTextColumns,
  selectCurrentViewColumnConfigs,
  selectCurrentViewSortData,
  (
    textColumns: TextColumn[],
    configs: ResultsColumnConfig[]|null,
    sortData: ResultsColumnSortData[],
  ): SortedResultsTableColumn[] => {

    const standardColumns: ResultsTableColumn[] = DEFAULT_RESULTS_COLUMNS.map(({ header, field }) => {
      const sortedObj = sortData.find(d => d.field === field);
      const obj: ResultsTableColumn = {
        header,
        field,
        type: 'standard',
        frozen: sortedObj?.frozen ?? false,
        minimisable: true,
        minimised: sortedObj?.minimised ?? false,
        wrappable: false,
        wrapped: sortedObj?.wrapped ?? false,
        hidden: sortedObj?.hidden ?? false,
        icon: {
          'description': 'bi bi-label bi-sm',
          'status': 'bi bi-check-circle bi-sm',
          'reports': 'bi bi-report bi-sm',
          'comment': 'bi bi-comment bi-sm',
          'hasAnnotations': 'bi bi-image bi-sm',
        }[field]
      };
      return obj;
    });


    const selectedTextColumns: ResultsTableColumn[] = textColumns.map(({ name, uuid }) => ({
      header: name,
      field: name,
      type: 'text',
      uuid,
      hidden: false,
      frozen: false,
      minimisable: true,
      wrappable: true,
      wrapped: sortData.find(d => d.field === name)?.wrapped ?? false,
      minimised: sortData.find(d => d.field === name)?.minimised ?? false,
    }));

    const getColType = ({ parameter, mapParameter, calculation }: ResultsColumnConfig): ResultsTableColumnType => {
      if (parameter) {
        return 'parameter';
      } else if (mapParameter) {
        return 'mapParameter';
      } else if (calculation) {
        return 'calculation';
      } else {
        return 'monitor';
      }
    };

    const selectedDataColumns: ResultsTableColumn[] = (configs || []).map((config) => ({
      header: config.displayName,
      field: config.property,
      type: getColType(config),
      hidden: false,
      frozen: false,
      minimisable: false,
      minimised: false,
      wrappable: false,
      wrapped: false,
      colorScale: config?.colorScale || 'none',
    }));

    return [
      ...standardColumns,
      ...selectedTextColumns,
      ...selectedDataColumns
    ].map(column => {
      const columnSortData = sortData.find(({ field, type }) => field === column.field && type === column.type);
      return { ...column, index: columnSortData?.index ?? 99 };
    }).sort((a, b) => a.index - b.index);
  }
);
